// /calendar — WeekPlanner page. Owns the anchor cursor, the planner data
// (four parallel sources via usePlannerEvents) and the task status mutation;
// the WeekPlanner component owns view/filter UI state.
//
// Deep-link: the cursor lives in the URL as ?week=YYYY-MM-DD (the anchor
// date as-is — invalid values fall back to the current week). Prev/next
// stepping rewrites the param with history replace (no entry pile-up); the
// Today button (any cursor landing on today) drops it, keeping the home URL
// canonical (same pattern as useDataViewPagination page=1).
//
// Quick-create: the planner zones open the existing Task/Meeting form
// modals preset to the picked day (no new surfaces). The entity mutations
// invalidate the ['tasks'|'meetings', workspaceId] key roots, which the
// planner queries sit under — the chips appear without extra wiring.

import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from '@govard/i18n-core/react';
import { Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useMemo, useState, type ReactElement } from 'react';

import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { useUpdateTaskMutation } from '../../tasks/hooks/useTaskMutations.js';
import { TaskFormModal } from '../../tasks/components/TaskFormModal.js';
import { MeetingFormModal } from '../../meetings/components/MeetingFormModal.js';
import { WeekPlanner } from '../components/WeekPlanner.js';
import type { QuickCreateRequest } from '../components/PlannerWeekView.js';
import { usePlannerEvents } from '../hooks/usePlannerEvents.js';
import {
  parseWeekParam,
  WEEK_URL_PARAM,
  WIRED_EVENT_TYPES,
  type PlannerEventType,
} from '../lib/plannerEvents.js';

interface QuickCreateState {
  request: QuickCreateRequest;
}

export default function WeekPlannerPage(): ReactElement {
  useI18nSubscription();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cursor, setCursor] = useState<Dayjs>(
    () => parseWeekParam(searchParams.get(WEEK_URL_PARAM)) ?? dayjs(),
  );
  const [quickCreate, setQuickCreate] = useState<QuickCreateState | null>(null);

  const { events, isLoading, isError, error, refetch, sourceFailures, canReviewTelegram } =
    usePlannerEvents(cursor);
  const updateTask = useUpdateTaskMutation();

  const availableTypes = useMemo<readonly PlannerEventType[]>(
    () =>
      canReviewTelegram ? WIRED_EVENT_TYPES : WIRED_EVENT_TYPES.filter((type) => type !== 'tg'),
    [canReviewTelegram],
  );

  const handleToggleTask = (taskId: string, done: boolean): void => {
    updateTask.mutate({ id: taskId, input: { status: done ? 'done' : 'todo' } });
  };

  const handleQuickCreate = (request: QuickCreateRequest): void => {
    setQuickCreate({ request });
  };

  const closeQuickCreate = (): void => {
    setQuickCreate(null);
  };

  const quickDate = quickCreate?.request.dateKey;
  // Preset defaults: tasks start at 09:00, meetings at 10:00 — the user can
  // adjust inside the modal; the date itself is already the picked day.
  const quickTaskDue = quickDate !== undefined ? `${quickDate}T09:00:00` : undefined;
  const quickMeetingStart = quickDate !== undefined ? `${quickDate}T10:00:00` : undefined;

  const handleCursorChange = (next: Dayjs): void => {
    setCursor(next);
    const nextParams = new URLSearchParams(searchParams);
    if (next.isSame(dayjs(), 'day')) {
      nextParams.delete(WEEK_URL_PARAM);
    } else {
      nextParams.set(WEEK_URL_PARAM, next.format('YYYY-MM-DD'));
    }
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <div className="animate-fade-in flex h-full flex-col">
      <PageHeader
        title={t('crm.calendar.title')}
        subtitle={t('crm.calendar.subtitle')}
        actions={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => void refetch()}
            aria-label={t('crm.calendar.refresh')}
            style={{ borderRadius: 10, height: 32, width: 32 }}
          />
        }
      />

      {isError ? (
        <ErrorState
          title={t('crm.calendar.load_error')}
          error={error}
          onRetry={() => void refetch()}
        />
      ) : (
        <>
          {/* Partial failure: each broken source gets its own compact notice
              above the calendar; the calendar itself keeps whatever loaded.
              Retry here refetches only the failed source. */}
          {sourceFailures.map((failure) => (
            <ErrorState
              key={failure.key}
              title={t(failure.titleKey as never)}
              fallbackDescription={t('crm.calendar.source_error.hint')}
              onRetry={() => void failure.retry()}
            />
          ))}
          <WeekPlanner
            events={events}
            cursor={cursor}
            onCursorChange={handleCursorChange}
            loading={isLoading}
            availableTypes={availableTypes}
            onToggleTask={handleToggleTask}
            onQuickCreate={handleQuickCreate}
          />
        </>
      )}

      <TaskFormModal
        open={quickCreate?.request.kind === 'task'}
        onClose={closeQuickCreate}
        {...(quickTaskDue !== undefined ? { initialDueAt: quickTaskDue } : {})}
      />
      <MeetingFormModal
        open={quickCreate?.request.kind === 'meeting'}
        onClose={closeQuickCreate}
        {...(quickMeetingStart !== undefined ? { initialStartsAt: quickMeetingStart } : {})}
      />
    </div>
  );
}
